<template>
    <div>
        <div class="LargeFormatSelfieDelegation">
            <div class="LargeFormatSelfieDelegation__visualAid">
                <img class="SelfieCaptureVisualAid" :src="imageUrl">
            </div>
            <div class="LargeFormatSelfieDelegation__instructions">
                <h2>{{ contents.heading }}</h2>
                <p>{{ contents.reason }}</p>
                <p>{{ contents.delegationInstructions }}</p>

                <div
                    v-if="delegationStatus === 'error'"
                    class="LargeFormatSelfieDelegation__error"
                    role="status"
                    data-test-id="SelfieDelegationForm__error"
                >
                    <p>
                        We couldn't build your QR code. Try again in a few minutes, or open the link
                        in your verification email directly on a mobile device.
                    </p>
                    <button
                        class="LargeFormatSelfieDelegation__retry"
                        type="button"
                        data-test-id="SelfieDelegationForm__retry"
                        @click="requestLink"
                    >
                        Try again
                    </button>
                </div>

                <template v-else-if="delegationUrl">
                    <QrCode
                        class="LargeFormatSelfieDelegation__qr"
                        :content="delegationUrl"
                        aria-label="Scan this QR code to take your selfie on your mobile device"
                        @render-error="handleCodeRenderError"
                    />
                    <div class="LargeFormatSelfieDelegation__urlWrapper">
                        <span class="LargeFormatSelfieDelegation__urlLabel">{{ urlLabel }}</span>
                        <span
                            class="LargeFormatSelfieDelegation__url"
                            data-test-id="SelfieDelegationForm__url"
                        ><var>{{ delegationUrl }}</var></span>
                    </div>
                </template>

                <p
                    v-else
                    class="LargeFormatSelfieDelegation__pending"
                    role="status"
                    data-test-id="SelfieDelegationForm__pending"
                >
                    Generating your QR code…
                </p>
            </div>
        </div>
        <QuestionFormControls v-bind="propsWithListenerAttrs" :has-submit="false" />
    </div>
</template>

<script lang="ts">
    import { Component, Prop, Watch } from 'vue-property-decorator';

    import selfieCaptureVisualAidUrl from '@evidentid/ido-lib/images/SelfieCaptureVisualAid.svg';

    import QrCode from '@/components/common/QrCode.vue';
    import QuestionFormControls from '@/components/forms/QuestionFormControls.vue';
    import contents from '@/config/selfieContents';
    import FieldComponent from '@/fields/FieldComponent';

    // Refresh this long before the link expires rather than at it, so a scan started just
    // before the boundary still lands on a live link.
    const REFRESH_MARGIN_MS = 60 * 1000;
    // Floor on the delay, so a short or already-elapsed TTL cannot turn into a request loop.
    const MIN_REFRESH_MS = 30 * 1000;

    @Component({
        emits: [ 'delegate' ],
        inheritAttrs: false,
        components: { QuestionFormControls, QrCode },
    })
    export default class SelfieDelegationForm extends FieldComponent<any> {
        private imageUrl = selfieCaptureVisualAidUrl;

        @Prop({ type: String, default: null })
        private delegationUrl!: string | null;

        @Prop({ type: Number, default: null })
        private delegationExpiresAt!: number | null;

        // Set when the code could not be drawn. The link below is the whole fallback, so the
        // label stops referring to a code the user cannot see.
        private codeUnavailable: boolean = false;

        private refreshTimer: number | null = null;

        private get copySource(): string {
            return this.question.metadata.copySource || 'vanillaSelfieContent';
        }

        private get contents() {
            return contents[this.copySource] || contents.vanillaSelfieContent;
        }

        private handleCodeRenderError() {
            this.codeUnavailable = true;
        }

        // A new URL gets its own attempt at drawing, so a failure on the previous one must
        // not leave the fallback label showing above a code that is now on screen.
        @Watch('delegationUrl')
        private onDelegationUrlChange() {
            this.codeUnavailable = false;
        }

        private get urlLabel(): string {
            return this.codeUnavailable
                ? 'Open this link on your phone to continue:'
                : 'Or open this link on your phone:';
        }

        private mounted() {
            this.requestLink();
            this.scheduleRefresh();
        }

        private unmounted() {
            this.clearRefresh();
        }

        // The link carries a server-side TTL measured in minutes, and this screen's whole
        // premise is that the user leaves it open while they work on their phone. Without a
        // refresh the desktop keeps painting a healthy-looking code that resolves to "Link not
        // found" on the phone, and the only recovery is a page reload the user has no reason
        // to attempt. Refreshing rather than erroring out keeps that invisible: `requestLink`
        // is deterministic for the session, so it returns the same URL with a fresh TTL and
        // the code on screen never changes.
        @Watch('delegationExpiresAt')
        private onDelegationExpiryChange() {
            this.scheduleRefresh();
        }

        private scheduleRefresh() {
            this.clearRefresh();

            if (!this.delegationExpiresAt) {
                return;
            }

            const msUntilRefresh = this.delegationExpiresAt * 1000 - Date.now() - REFRESH_MARGIN_MS;

            // Floored, so an already-elapsed or very short TTL cannot become a request loop.
            this.refreshTimer = window.setTimeout(
                () => this.requestLink(),
                Math.max(msUntilRefresh, MIN_REFRESH_MS),
            );
        }

        private clearRefresh() {
            if (this.refreshTimer !== null) {
                window.clearTimeout(this.refreshTimer);
                this.refreshTimer = null;
            }
        }

        private requestLink() {
            this.$emit('delegate');
        }
    }
</script>

<style lang="less">
    .LargeFormatSelfieDelegation {
        &__qr {
            margin: 1.5rem 0 1rem;
        }

        &__urlWrapper {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
            margin-bottom: 1.5rem;
        }

        &__urlLabel {
            font-size: 0.875rem;
        }

        // A hashed token has no word boundaries, so without an explicit rule it overflows
        // its container. `anywhere` breaks only when the line cannot otherwise fit.
        &__url {
            font-size: 0.875rem;
            font-weight: bold;
            overflow-wrap: anywhere;
            user-select: all;
        }

        &__pending {
            margin: 2rem 0;
        }
    }
</style>
